import React from 'react';
import { alpha } from '@mui/material/styles';
import { buttonBaseClasses } from '@mui/material/ButtonBase';
import { dividerClasses } from '@mui/material/Divider';
import { menuItemClasses } from '@mui/material/MenuItem';
import { selectClasses } from '@mui/material/Select';
import { tabClasses } from '@mui/material/Tab';
import { gray, brand } from '../ThemePrimitives';
import { UnfoldMoreRounded } from '@mui/icons-material';

export const navigationCustomizations = {
  MuiMenuItem: {
    styleOverrides: {
      root: ({ theme }) => ({
        borderRadius: theme.shape.borderRadius,
        padding: '8px 12px',
        margin: '2px 4px',
        transition: 'all 0.2s ease',
        [`&.${menuItemClasses.focusVisible}`]: {
          backgroundColor: 'transparent',
        },
        [`&.${menuItemClasses.selected}`]: {
          backgroundColor: alpha(theme.palette.primary.main, 0.08),
          color: theme.palette.primary.main,
          [`&.${menuItemClasses.focusVisible}`]: {
            backgroundColor: alpha(theme.palette.action.selected, 0.3),
          },
          '&:hover': {
            backgroundColor: alpha(theme.palette.primary.main, 0.12),
          },
        },
        '&:hover': {
          backgroundColor: alpha(theme.palette.primary.main, 0.04),
        },
        ...theme.applyStyles('dark', {
          [`&.${menuItemClasses.selected}`]: {
            backgroundColor: alpha(theme.palette.primary.main, 0.15),
          },
          '&:hover': {
            backgroundColor: alpha(theme.palette.primary.main, 0.08),
          },
        }),
      }),
    },
  },
  MuiMenu: {
    styleOverrides: {
      list: {
        gap: '2px',
        padding: '4px',
        [`&.${dividerClasses.root}`]: {
          margin: '0 -4px',
        },
      },
      paper: ({ theme }) => ({
        marginTop: '4px',
        borderRadius: theme.shape.borderRadius,
        border: `1px solid ${alpha(theme.palette.divider, 0.4)}`,
        backgroundImage: 'none',
        background: alpha(theme.palette.background.paper, 0.98),
        backdropFilter: 'blur(8px)',
        padding: '4px',
        [`& .${buttonBaseClasses.root}`]: {
          '&.Mui-selected': {
            backgroundColor: alpha(theme.palette.action.selected, 0.3),
          },
        },
        ...theme.applyStyles('dark', {
          background: alpha(gray[900], 0.98),
        }),
      }),
    },
  },
  MuiSelect: {
    defaultProps: {
      IconComponent: React.forwardRef((props, ref) => (
        <UnfoldMoreRounded fontSize="small" {...props} ref={ref} />
      )),
    },
    styleOverrides: {
      root: ({ theme }) => ({
        borderRadius: theme.shape.borderRadius,
        border: '1px solid',
        borderColor: alpha(gray[200], 0.5),
        backgroundColor: alpha(theme.palette.primary.light, 0.02),
        transition: 'all 0.2s ease',
        '&:hover': {
          borderColor: brand[300],
          backgroundColor: alpha(theme.palette.primary.light, 0.04),
        },
        [`&.${selectClasses.focused}`]: {
          outlineOffset: 0,
          borderColor: brand[400],
        },
        '&:before, &:after': {
          display: 'none',
        },
        ...theme.applyStyles('dark', {
          borderColor: alpha(gray[700], 0.5),
          backgroundColor: alpha(gray[800], 0.2),
          '&:hover': {
            borderColor: brand[400],
            backgroundColor: alpha(gray[700], 0.3),
          },
          [`&.${selectClasses.focused}`]: {
            borderColor: brand[500],
          },
        }),
      }),
      select: ({ theme }) => ({
        display: 'flex',
        alignItems: 'center',
        padding: '1px 2px',
        fontSize: '0.875rem',
        fontWeight: 500,
        '&:focus-visible': {
          backgroundColor: alpha(theme.palette.primary.light, 0.04),
        },
        ...theme.applyStyles('dark', {
          '&:focus-visible': {
            backgroundColor: alpha(gray[800], 0.3),
          },
        }),
      }),
    },
  },
  MuiLink: {
    defaultProps: {
      underline: 'none',
    },
    styleOverrides: {
      root: ({ theme }) => ({
        color: theme.palette.text.primary,
        fontWeight: 500,
        position: 'relative',
        textDecoration: 'none',
        width: 'fit-content',
        transition: 'all 0.3s ease',
        '&::before': {
          content: '""',
          position: 'absolute',
          width: '100%',
          height: '2px',
          bottom: -2,
          left: 0,
          backgroundColor: theme.palette.primary.main,
          opacity: 0,
          transition: 'all 0.3s ease',
          borderRadius: theme.shape.borderRadius,
        },
        '&:hover::before': {
          opacity: 0.6,
          width: '100%',
        },
        '&:hover': {
          color: theme.palette.primary.main,
        },
        '&:focus-visible': {
          outline: `2px solid ${alpha(brand[500], 0.4)}`,
          outlineOffset: '4px',
          borderRadius: theme.shape.borderRadius,
        },
      }),
    },
  },
  MuiDrawer: {
    styleOverrides: {
      paper: ({ theme }) => ({
        backgroundColor: theme.palette.background.default,
      }),
    },
  },
  MuiPaginationItem: {
    styleOverrides: {
      root: ({ theme }) => ({
        borderRadius: theme.shape.borderRadius,
        margin: '0 2px',
        transition: 'all 0.2s ease',
        '&:hover': {
          backgroundColor: alpha(theme.palette.primary.main, 0.04),
        },
        '&.Mui-selected': {
          color: 'white',
          backgroundColor: theme.palette.primary.main,
          '&:hover': {
            backgroundColor: theme.palette.primary.dark,
          },
        },
        ...theme.applyStyles('dark', {
          '&.Mui-selected': {
            color: 'white',
            backgroundColor: theme.palette.primary.main,
          },
        }),
      }),
    },
  },
  MuiTabs: {
    styleOverrides: {
      root: { minHeight: 'fit-content' },
      indicator: ({ theme }) => ({
        backgroundColor: theme.palette.grey[800],
        ...theme.applyStyles('dark', {
          backgroundColor: theme.palette.grey[200],
        }),
      }),
    },
  },
  MuiTab: {
    styleOverrides: {
      root: ({ theme }) => ({
        padding: '8px 16px',
        margin: '2px',
        textTransform: 'none',
        minWidth: 'fit-content',
        minHeight: 'fit-content',
        color: theme.palette.text.secondary,
        borderRadius: theme.shape.borderRadius,
        border: '1px solid',
        borderColor: 'transparent',
        transition: 'all 0.2s ease',
        fontWeight: 500,
        fontSize: '0.875rem',
        ':hover': {
          color: theme.palette.text.primary,
          backgroundColor: alpha(theme.palette.primary.main, 0.04),
          borderColor: alpha(theme.palette.primary.main, 0.1),
        },
        [`&.${tabClasses.selected}`]: {
          color: theme.palette.primary.main,
          backgroundColor: alpha(theme.palette.primary.main, 0.08),
          borderColor: alpha(theme.palette.primary.main, 0.1),
        },
        ...theme.applyStyles('dark', {
          ':hover': {
            color: theme.palette.text.primary,
            backgroundColor: alpha(theme.palette.primary.main, 0.08),
            borderColor: alpha(theme.palette.primary.main, 0.15),
          },
          [`&.${tabClasses.selected}`]: {
            color: theme.palette.primary.light,
            backgroundColor: alpha(theme.palette.primary.main, 0.15),
          },
        }),
      }),
    },
  },
  MuiStepConnector: {
    styleOverrides: {
      line: ({ theme }) => ({
        borderTop: '2px solid',
        borderColor: alpha(theme.palette.primary.main, 0.2),
        flex: 1,
        borderRadius: '99px',
      }),
    },
  },
  MuiStepIcon: {
    styleOverrides: {
      root: ({ theme }) => ({
        color: 'transparent',
        border: `2px solid ${alpha(gray[400], 0.3)}`,
        width: 14,
        height: 14,
        borderRadius: '50%',
        transition: 'all 0.3s ease',
        '& text': {
          display: 'none',
        },
        '&.Mui-active': {
          border: 'none',
          color: theme.palette.primary.main,
        },
        '&.Mui-completed': {
          border: 'none',
          color: theme.palette.success.main,
        },
      }),
    },
  },
  MuiStepLabel: {
    styleOverrides: {
      label: ({ theme }) => ({
        fontWeight: 500,
        fontSize: '0.875rem',
        '&.Mui-completed': {
          opacity: 0.6,
        },
        '&.Mui-active': {
          color: theme.palette.primary.main,
          fontWeight: 600,
        },
      }),
    },
  },
};
